import { Check } from 'lucide-react-native';
import { useEffect } from 'react';
import { Platform, Pressable, StyleSheet, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useSpringPress } from '../../hooks/useSpringPress';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { darkPrecisionTypography } from '../../theme/typography';
import { Text } from './Text';

interface ChecklistItemProps {
  label: string;
  checked: boolean;
  onPress?: () => void;
  ctaLabel?: string;
  onCtaPress?: () => void;
  style?: ViewStyle;
}

export function ChecklistItem({
  label,
  checked,
  onPress,
  ctaLabel = 'Go',
  onCtaPress,
  style,
}: Readonly<ChecklistItemProps>) {
  const reduced = useReducedMotion();
  const fill = useSharedValue(checked ? 1 : 0);
  const check = useSharedValue(checked ? 1 : 0);
  const { style: pressStyle, onPressIn, onPressOut } = useSpringPress();

  useEffect(() => {
    const duration = reduced ? 0 : 280;
    const easing = Easing.bezier(0.22, 1, 0.36, 1);
    fill.value = withTiming(checked ? 1 : 0, { duration, easing });
    check.value = withTiming(checked ? 1 : 0, { duration: duration * 0.7, easing });
  }, [checked, reduced, fill, check]);

  const boxStyle = useAnimatedStyle(() => ({
    backgroundColor: fill.value > 0.5 ? colors.primary.main : 'transparent',
    borderColor: fill.value > 0.05 ? colors.primary.main : colors.border.strong,
  }));

  const checkStyle = useAnimatedStyle(() => ({
    opacity: check.value,
    transform: [{ scale: 0.6 + check.value * 0.4 }],
  }));

  return (
    <Animated.View style={[styles.container, pressStyle, style]}>
      <Pressable
        accessibilityRole="checkbox"
        accessibilityState={{ checked }}
        accessibilityLabel={label}
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        style={styles.checkboxWrapper}
        hitSlop={8}
      >
        <Animated.View style={[styles.checkbox, boxStyle]}>
          <Animated.View style={checkStyle}>
            <Check size={14} color={colors.background.primary} strokeWidth={3} />
          </Animated.View>
        </Animated.View>
      </Pressable>
      <Text
        style={[darkPrecisionTypography.body, styles.label, checked && styles.labelChecked]}
        numberOfLines={2}
      >
        {label}
      </Text>
      {!checked && onCtaPress && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={ctaLabel}
          onPress={onCtaPress}
          style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
        >
          <Text style={styles.ctaText}>{ctaLabel}</Text>
        </Pressable>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.sm,
    gap: tokens.spacing.md,
  },
  checkboxWrapper: {
    width: 22,
    height: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    flex: 1,
    color: colors.text.primary,
  },
  labelChecked: {
    color: colors.text.secondary,
    textDecorationLine: Platform.OS === 'web' ? 'line-through' : 'none',
  },
  cta: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.primary.faint,
  },
  ctaPressed: {
    opacity: 0.7,
  },
  ctaText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary.main,
    letterSpacing: 0.3,
  },
});
